/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { CustomText, CustomLabel6 } from '@4beefopag/4bee-fopag-core/dist/atoms';
import { moneyFormat } from '@4beefopag/4bee-fopag-core/dist/util';
import { InfoCircleFilled } from '@ant-design/icons';
import { Tooltip, Input as InputNumber, Spin } from 'antd';
import { faAlarmClock, faCalendarExclamation, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mostrarErro } from '@4beefopag/4bee-fopag-core/dist/molecules';
import { APONTAMENTO, gravarValorCadastro, PAYROLL_ACTOR_KIND } from '../../../services/apontamentoProducao';
import { LinhaColaborador, InputProducao } from '../../atoms/Apontamento';
import InputWithChangedValue from '../../atoms/Apontamento/InputWithChangedValue';
import ValorHora from '../../atoms/ValorHora';
import RemuneracaoBase from '../../atoms/RemuneracaoBase';
import { colaboradorProps, parametrizacaoProps } from '../../atoms/Props';
import { Cell } from '../../atoms/Table';
import NomeColaboradorCell from './NomeColaboradorCell';
import { shouldBlockUnsignedContract } from '../../../services/util/rule';
import { roundNumber } from '../../../services/util/number';

function ColaboradorMensal({
  itemMensal,
  payrollId,
  isEditable,
  onChangeItemValues,
  parametrizacao,
  createdByActorKind,
}) {
  const { cooperator } = itemMensal;
  const showApiValueIndicator = createdByActorKind === PAYROLL_ACTOR_KIND.M2M;

  const onChangeHourlyRate = (hourlyRate) => {
    const producao = roundNumber(itemMensal.baseHora * hourlyRate, 4);
    onChangeItemValues(itemMensal.id, {
      hourlyRate,
      producao,
      monthlyEstimate: producao,
      cooperator: {
        ...cooperator,
        hourlyRate,
        monthlyEstimate: producao,
      },
    });
  };

  const onChangeBaseSalary = (monthlyEstimate) => {
    let hourlyRate = 0;
    if (itemMensal.baseHora > 0) {
      hourlyRate = roundNumber(monthlyEstimate / itemMensal.baseHora, 4);
    }
    onChangeItemValues(itemMensal.id, {
      hourlyRate,
      producao: monthlyEstimate,
      cooperator: {
        ...cooperator,
        hourlyRate,
        monthlyEstimate,
      },
    });
  };

  let icon = <InfoCircleFilled style={{ marginRight: 8 }} />;

  const [loadingHoristaMensalista, setLoadingHoristaMensalista] = useState(false);
  async function alterarHoristaMensalista() {
    if (isEditable) {
      setLoadingHoristaMensalista(true);

      const novoHoristaMensalista =
        itemMensal.horistaMensalista === APONTAMENTO.horistaMensalista.mensalista
          ? APONTAMENTO.horistaMensalista.horista
          : APONTAMENTO.horistaMensalista.mensalista;

      onChangeItemValues(itemMensal.id, {
        horistaMensalista: novoHoristaMensalista,
      });

      try {
        await gravarValorCadastro(
          payrollId,
          itemMensal.cooperator.id,
          itemMensal.hourlyRate,
          itemMensal.monthlyEstimate,
          novoHoristaMensalista
        );
      } catch (err) {
        mostrarErro(err);
      }

      setLoadingHoristaMensalista(false);
    }
  }

  const isColabBlocked = shouldBlockUnsignedContract(cooperator, parametrizacao) || !cooperator.isEsocialIncluded;

  if (loadingHoristaMensalista) {
    icon = (
      <Spin
        size="small"
        style={{ marginRight: 8 }}
      />
    );
  } else if (isColabBlocked) {
    const title = !cooperator.isEsocialIncluded
      ? 'Cooperado deve regularizar a pendência do e-social para habilitar a edição'
      : 'Cooperado deve assinar o contrato para habilitar a edição';
    icon = (
      <Tooltip title={title}>
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          size="sm"
          style={{ marginRight: 8, cursor: isEditable ? 'pointer' : 'not-allowed' }}
        />
      </Tooltip>
    );
  } else if (itemMensal.horistaMensalista === APONTAMENTO.horistaMensalista.mensalista) {
    icon = (
      <Tooltip title="Mensalista">
        <FontAwesomeIcon
          icon={faCalendarExclamation}
          size="sm"
          style={{
            marginRight: 8,
            cursor: isEditable || cooperator.hasSignedContract === 1 ? 'pointer' : 'not-allowed',
          }}
          onClick={() => alterarHoristaMensalista()}
        />
      </Tooltip>
    );
  } else if (itemMensal.horistaMensalista === APONTAMENTO.horistaMensalista.horista) {
    icon = (
      <Tooltip title="Horista">
        <FontAwesomeIcon
          icon={faAlarmClock}
          size="sm"
          style={{ marginRight: 8, cursor: isEditable ? 'pointer' : 'not-allowed' }}
          onClick={() => alterarHoristaMensalista()}
        />
      </Tooltip>
    );
  }

  function validarProducaoDiferente() {
    const producao = itemMensal.producao || 0;
    const monthlyEstimate = itemMensal.monthlyEstimate || 0;

    return producao !== monthlyEstimate;
  }

  return (
    <LinhaColaborador
      semValor={itemMensal.producao === 0}
      key={itemMensal.id}
      semContrato={isColabBlocked}
      destaque={itemMensal.valorHoraAlteradoParaCompetencia}
    >
      <Cell
        fixed
        className="cpf"
        width="100px"
        left="0px"
      >
        <CustomLabel6>{cooperator.cpf}</CustomLabel6>
      </Cell>
      <NomeColaboradorCell
        icon={icon}
        colaborador={cooperator}
      />
      <td className="dias-trabalhados">
        <InputNumber
          type="number"
          min={0}
          value={itemMensal.diasTrabalhados}
          onChange={(e) => {
            const diasTrabalhados = e.target.value;
            const producao = roundNumber(roundNumber(itemMensal.monthlyEstimate / 30, 4) * diasTrabalhados, 4);

            onChangeItemValues(itemMensal.id, {
              diasTrabalhados,
              producao,
            });
          }}
          disabled={
            itemMensal.horistaMensalista === APONTAMENTO.horistaMensalista.horista || !isEditable || isColabBlocked
          }
        />
      </td>
      <td className="horas-trabalhadas">
        <InputNumber
          type="number"
          min={0}
          value={itemMensal.baseHora}
          onChange={(e) => {
            const baseHora = e.target.value;
            const producao = baseHora * itemMensal.hourlyRate;
            onChangeItemValues(itemMensal.id, {
              baseHora,
              producao,
            });
          }}
          disabled={
            itemMensal.horistaMensalista === APONTAMENTO.horistaMensalista.mensalista || !isEditable || isColabBlocked
          }
        />
      </td>
      <td className="valor-hora">
        <ValorHora
          hourlyRate={cooperator.hourlyRate}
          horistaMensalista={itemMensal.horistaMensalista}
          onChange={onChangeHourlyRate}
          requisicao={payrollId}
          contratado={itemMensal.cooperator.id}
          permiteEditar={isEditable && !isColabBlocked && !parametrizacao.ga_should_sync_earning_values}
        />
      </td>
      <td className="remuneracao-base">
        <RemuneracaoBase
          monthlyEstimate={cooperator.monthlyEstimate}
          horistaMensalista={itemMensal.horistaMensalista}
          onChange={onChangeBaseSalary}
          requisicao={payrollId}
          contratado={itemMensal.cooperator.id}
          permiteEditar={isEditable && !isColabBlocked && !parametrizacao.ga_should_sync_earning_values}
        />
      </td>
      <td className="produção">
        <Tooltip title={validarProducaoDiferente() ? 'Valor de produção foi alterado para o lançamento atual' : ''}>
          <InputProducao producaoDiferente={validarProducaoDiferente()}>
            <InputWithChangedValue
              min={0}
              prefix="R$"
              value={itemMensal.producao}
              originalValue={itemMensal.originalValues?.producao}
              showIndicator={showApiValueIndicator}
              getValue={(producao) => {
                onChangeItemValues(itemMensal.id, {
                  producao,
                });
              }}
              disabled={!isEditable || isColabBlocked}
            />
          </InputProducao>
        </Tooltip>
      </td>
      <td className="variável">
        <InputWithChangedValue
          min={0}
          prefix="R$"
          value={itemMensal.variavel}
          originalValue={itemMensal.originalValues?.variavel}
          showIndicator={showApiValueIndicator}
          getValue={(variavel) => {
            onChangeItemValues(itemMensal.id, {
              variavel,
            });
          }}
          disabled={!isEditable || isColabBlocked}
        />
      </td>
      <td className="reemb">
        <InputWithChangedValue
          min={0}
          prefix="R$"
          value={itemMensal.reembolso}
          originalValue={itemMensal.originalValues?.reembolso}
          showIndicator={showApiValueIndicator}
          getValue={(reembolso) => {
            onChangeItemValues(itemMensal.id, {
              reembolso,
            });
          }}
          disabled={!isEditable || isColabBlocked}
        />
      </td>
      {itemMensal.totalRendimentos < itemMensal.adiantamento ? (
        <Tooltip title="Adiantamento superou o Total de Rendimentos">
          <td style={{ minWidth: '120px' }}>
            <CustomText
              color="var(--error)"
              ellipsis
              strong
            >
              {moneyFormat(itemMensal.totalRendimentos)}
            </CustomText>
          </td>
        </Tooltip>
      ) : (
        <td
          className="total-rend"
          style={{ minWidth: '120px' }}
        >
          <CustomText
            ellipsis
            strong
          >
            {moneyFormat(itemMensal.totalRendimentos)}
          </CustomText>
        </td>
      )}
      <td className="descontos-diversos">
        <InputWithChangedValue
          prefix="R$"
          value={itemMensal.descontosDiversos}
          originalValue={itemMensal.originalValues?.descontosDiversos}
          showIndicator={showApiValueIndicator}
          getValue={(descontosDiversos) => {
            onChangeItemValues(itemMensal.id, {
              descontosDiversos,
            });
          }}
          disabled={!isEditable || isColabBlocked}
        />
      </td>
      <td className="adiantamento">
        <InputWithChangedValue
          prefix="R$"
          value={itemMensal.adiantamento}
          originalValue={itemMensal.originalValues?.adiantamento}
          showIndicator={showApiValueIndicator}
          getValue={(adiantamento) => {
            onChangeItemValues(itemMensal.id, {
              adiantamento,
            });
          }}
          disabled={!isEditable || isColabBlocked}
        />
      </td>
      <td
        className="total-débitos"
        style={{ minWidth: '120px' }}
      >
        <CustomText
          ellipsis
          strong
        >
          {moneyFormat(itemMensal.totalDebitos)}
        </CustomText>
      </td>
      <td
        className="líquido"
        style={{ minWidth: '120px' }}
      >
        <CustomText
          ellipsis
          strong
        >
          {moneyFormat(itemMensal.liquido)}
        </CustomText>
      </td>
    </LinhaColaborador>
  );
}

ColaboradorMensal.propTypes = {
  itemMensal: colaboradorProps,
  onChangeItemValues: PropTypes.func.isRequired,
  payrollId: PropTypes.number.isRequired,
  isEditable: PropTypes.bool.isRequired,
  parametrizacao: parametrizacaoProps,
  createdByActorKind: PropTypes.string,
};

ColaboradorMensal.defaultProps = {
  itemMensal: undefined,
  parametrizacao: {},
  createdByActorKind: undefined,
};

export default memo(ColaboradorMensal);
